// src/routes.ts
import { lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import LoginPage from "../pages/LoginPage";
import ProtectedRoute from "../components/common/ProtectedRoute";
import UnAuthorizedPage from "../pages/UnAuthorizedPage";
import AuthLayout from "../components/layout/AuthLayout";
import RegisterPage from "../pages/user/RegisterPage";
import OtpPage from "../pages/user/OtpPage";
import ResetPage from "../pages/user/ResetPage";
import KycPage from "../pages/user/KycPage";
import DashboardLayout from "../components/layout/DashboardLayout";
import SettingsLayout from "../components/layout/SettingsLayout";
import EmailResetForm from "../pages/user/EmailResetForm";
import ChangePassword from "../pages/user/ChangePassword";
import AdminDashboard from "../pages/admin/AdminDashboard";
import ResendEmailResetForm from "../pages/user/ResendEmailResetForm";
import KycUnderReview from "../pages/user/KycUnderReview";
import ManageRoles from "../pages/admin/ManageRoles";
import AuthRedirect from "../components/common/AuthRedirection";
import RequireOtpGuard from "../guards/RequireOtp";
import PaymentLink from "../components/features/PaymentLink/PaymentLink";
import AddWithdrawalInfo from "../pages/user/AddWithdrawalInfo";

const DashboardPage = lazy(() => import("../pages/user/DashboardPage"));
const ProfileDetailsPage = lazy(() => import("../pages/user/ProfileDetails"));
const PayoutConfig = lazy(() => import("../pages/user/PayoutConfig"));
const ZonesServicesPayIn = lazy(
  () => import("../pages/user/payin/./ZonesAndServicesPayIn"),
);
const ZonesServicesPayOut = lazy(
  () => import("../pages/user/payout/./ZonesAndServicesPayOut")
);
const DevelopersPage = lazy(() => import("../pages/user/Developers"));

const NoMatchPage = lazy(() => import("../pages/NoMatchPage"));
const ManageKycPage = lazy(() => import("../pages/admin/Customers"));
const ManageDocumentType = lazy(
  () => import("../pages/admin/ManageDocumentType"),
);
const MonetaryZonesPages = lazy(
  () => import("../pages/admin/MonetaryZonesPage"),
);
const CountriesMonetaryZones = lazy(
  () => import("../pages/admin/CountriesMonetaryZones"),
);
const CountriesDetails = lazy(() => import("../pages/admin/CountryDetails"));
const ServicesPage = lazy(() => import("../pages/admin/ServicesPage"));
const CustomerDetailsPage = lazy(
  () => import("../pages/admin/CustomerDetailsPage"),
);
const TransactionsHistory = lazy(
  () => import("../pages/admin/TransactionsHistory"),
);

const UserPayInTransactionHistory = lazy(
  () => import("../pages/user/payin/./UserPayInTransactionHistory"),
);
const UserPayOutTransactionHistory = lazy(
  () => import("../pages/user/payout/./UserPayOutTransactionHistory"),
);
const UserPayOutFundingsHistory = lazy(
  () => import("../pages/user/payout/./UserPayOutFundingsHistory"),
);
const PartnersPage = lazy(() => import("../pages/admin/PartnersPage"));
const PartnerDetails = lazy(() => import("../pages/admin/PartnerDetails"));
const WhitelistIpAdress = lazy(() => import("../pages/admin/ManageIpAdress"));
const WithdrwalsPage = lazy(() => import("../pages/admin/ManageWithdrawals"));
const ManagePaymentLink = lazy(() => import("../pages/user/ManagePaymentLink"));
const PaymentLinkList = lazy(() => import("../pages/admin/PaymentLinkList"));
const WithdrawalList = lazy(() => import("../pages/user/WithdrawalList"));
const Webhook = lazy(() => import("../pages/user/ManageWebhook"));
const RolePermissions = lazy(() => import("../pages/admin/RolesPermissions"));
const TransactionLog = lazy(() => import("../pages/admin/TransactionLog"));
const Maintenance = lazy(() => import("../pages/user/Maintenance"));
const MaintenanceAdmin = lazy(() => import("../pages/admin/Maintenance"));
const Users = lazy(() => import("../pages/admin/Users"));
const RouteConfig = lazy(() => import("../pages/admin/RouteConfig"));
const Payout = lazy(() => import("../pages/admin/Payout"));
const AuditLogs = lazy(() => import("../pages/admin/AuditLogs"));

// user routes
const routes = createBrowserRouter([
  {
    path: "/payments/:id_link",
    element: <PaymentLink/>,
  },
  {
    path: "/",
    element: <Navigate to="/auth" replace />,
  },
  {
    path: "auth",
    element: <AuthLayout />, // Wrapping auth pages in AuthLayout
    children: [
      {
        path: "",
        element: (
          <AuthRedirect>
            <LoginPage />
          </AuthRedirect>
        ),
      }, // Matches "/auth"
      {
        path: "register",
        element: (
          <AuthRedirect>
            <RegisterPage />
          </AuthRedirect>
        ),
      },
      {
        path: "forgot-password",
        element: (
          <AuthRedirect>
              <EmailResetForm />
          </AuthRedirect>
        ),
      },
      {
        path: "resend-email",
        element: (
          <AuthRedirect>
            <ResendEmailResetForm />
          </AuthRedirect>
        ),
      },
      { path: "otp", element: <OtpPage /> },
      { path: "reset-password", element: <ResetPage /> },
      { path: "kyc", element: <KycPage /> },
    ],
  },
  {
    path: "/dashboard",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ENTERPRISE", "CUSTOMER"]}>
              <DashboardPage />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "payin",
        children: [
          {
            path: "history",
            element: (
                <RequireOtpGuard>
                  <ProtectedRoute allowedRoles={["ENTERPRISE", "CUSTOMER"]}>
                    <UserPayInTransactionHistory />
                  </ProtectedRoute>
                </RequireOtpGuard>
            ),
          },
          {
            path: "services",
            element: (
                <RequireOtpGuard>
                  <ProtectedRoute allowedRoles={["ENTERPRISE", "CUSTOMER"]}>
                    <ZonesServicesPayIn />
                  </ProtectedRoute>
                </RequireOtpGuard>
            ),
          },
        ]
      },
      {
        path: "payout",
        children: [
          {
            path: "history",
            element: (
                <RequireOtpGuard>
                  <ProtectedRoute allowedRoles={["ENTERPRISE", "CUSTOMER"]}>
                    <UserPayOutTransactionHistory />
                  </ProtectedRoute>
                </RequireOtpGuard>
            ),
          },
          {
            path: "services",
            element: (
                <RequireOtpGuard>
                  <ProtectedRoute allowedRoles={["ENTERPRISE", "CUSTOMER"]}>
                    <ZonesServicesPayOut />
                  </ProtectedRoute>
                </RequireOtpGuard>
            ),
          },
          {
            path: "fundings-history",
            element: (
              <RequireOtpGuard>
                <ProtectedRoute allowedRoles={["ENTERPRISE", "CUSTOMER"]}>
                  <UserPayOutFundingsHistory />
                </ProtectedRoute>
              </RequireOtpGuard>
            ),
          },
        ]
      },

      {
        path: "developers",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ENTERPRISE", "CUSTOMER"]}>
              <DevelopersPage />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "payment-link",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ENTERPRISE", "CUSTOMER"]}>
              <ManagePaymentLink />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "manage-webhook",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ENTERPRISE", "CUSTOMER"]}>
              <Webhook />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "withdrawal-list",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ENTERPRISE", "CUSTOMER"]}>
              <WithdrawalList />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "settings",
        element: <SettingsLayout />,
        children: [
          {
            path: "",
            element: (
              <RequireOtpGuard>
                <ProtectedRoute allowedRoles={["ENTERPRISE", "CUSTOMER", "SUPERADMIN", "ADMIN", "REVIEWER"]}>
                  <ProfileDetailsPage />
                </ProtectedRoute>
              </RequireOtpGuard>
            ),
          },
          {
            path: "profile/change-password",
            element: (
              <RequireOtpGuard>
                <ProtectedRoute allowedRoles={["ENTERPRISE", "CUSTOMER", "SUPERADMIN", "ADMIN", "REVIEWER"]}>
                  <ChangePassword />
                </ProtectedRoute>
              </RequireOtpGuard>
            ),
          },
          {
            path: "payout",
            element: (
              <RequireOtpGuard>
                <ProtectedRoute allowedRoles={["ENTERPRISE", "CUSTOMER"]}>
                  <PayoutConfig />
                </ProtectedRoute>
              </RequireOtpGuard>
            ),
          },
          {
            path: "profile/withdrawal-account",
            element: (
              <RequireOtpGuard>
                <ProtectedRoute allowedRoles={["ENTERPRISE", "CUSTOMER", "SUPERADMIN", "ADMIN", "REVIEWER"]}>
                  <AddWithdrawalInfo />
                </ProtectedRoute>
              </RequireOtpGuard>
            ),
          },
        ],
      },
    ],
  },
  {
    path: "/admin",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN", "REVIEWER"]}>
              <AdminDashboard />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "customers",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN", "REVIEWER"]}>
              <ManageKycPage />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "users",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <Users />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "manage-withdrawals",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <WithdrwalsPage />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "payment-link",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <PaymentLinkList />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "manage-roles",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["SUPERADMIN", "ADMIN"]}>
              <ManageRoles />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "role-permission/:roleId",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["SUPERADMIN", "ADMIN"]}>
              <RolePermissions />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "customers/:customerId",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN", "REVIEWER"]}>
              <CustomerDetailsPage />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "manage-document",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN", "REVIEWER"]}>
              <ManageDocumentType />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "partners",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <PartnersPage />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "partners/:id",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <PartnerDetails />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "manage-monetary-zone",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <MonetaryZonesPages />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "payout",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <Payout />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "countries-monetary-zone",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <CountriesMonetaryZones />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "manage-service",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <ServicesPage />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "country-details/:countryId",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <CountriesDetails />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "manage-ip-address",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <WhitelistIpAdress />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "transaction-history",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <TransactionsHistory />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "transaction-log",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <TransactionLog />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "maintenance",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <MaintenanceAdmin />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "route-config",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <RouteConfig />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
      {
        path: "audit-logs",
        element: (
          <RequireOtpGuard>
            <ProtectedRoute allowedRoles={["ADMIN", "SUPERADMIN"]}>
              <AuditLogs />
            </ProtectedRoute>
          </RequireOtpGuard>
        ),
      },
    ],
  },

  {
    path: "/unauthorized",
    element: <UnAuthorizedPage />,
  },
  {
    path: "*",
    element: <NoMatchPage />,
  },
  { path: "kyc-under-review", element: <KycUnderReview /> },
  { path: "maintenance", element: <Maintenance /> },
]);

export default routes;
